import { observer } from "mobx-react";
import React, { FC, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { useAppStore } from "../../../../hooks/useAppStore";
import { isValidIpOrCidr } from "../../../helpers/ipHelper";
import { ApiKeySettingItem } from "../../../../services/apiService";

const SAVED_RESET_MS = 2000;

interface IpAllowlistBoxProps {
  isOpen: boolean;
  apiKey: ApiKeySettingItem;
}

const IpAllowlistBox: FC<IpAllowlistBoxProps> = observer(({ isOpen, apiKey }) => {
  const theme = useTheme();
  const appStore = useAppStore();

  const [allowlistDraft, setAllowlistDraft] = useState<string[]>([]);
  const [allowlistInput, setAllowlistInput] = useState("");
  const [allowlistInputError, setAllowlistInputError] = useState<string | null>(
    null,
  );
  const [allowlistSaved, setAllowlistSaved] = useState(false);
  const allowlistSavedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    if (isOpen && apiKey.id) {
      setAllowlistDraft(apiKey.ip_allowlist ?? []);
      setAllowlistInput("");
      setAllowlistInputError(null);
    }
  }, [isOpen, apiKey.id]);

  useEffect(() => {
    return () => {
      if (allowlistSavedTimeoutRef.current) {
        clearTimeout(allowlistSavedTimeoutRef.current);
      }
    };
  }, []);

  const isAllowlistDirty =
    JSON.stringify(allowlistDraft) !== JSON.stringify(apiKey.ip_allowlist ?? []);

  const handleAllowlistInputChange = (value: string) => {
    setAllowlistInput(value);
    if (allowlistInputError) {
      setAllowlistInputError(null);
    }
  };

  const handleAddAllowlistEntry = () => {
    const entry = allowlistInput.trim();
    if (!entry) return;

    if (!isValidIpOrCidr(entry)) {
      setAllowlistInputError(
        "Enter a valid IP address or CIDR range (e.g. 203.0.113.10 or 198.51.100.0/24).",
      );
      return;
    }

    if (allowlistDraft.includes(entry)) {
      setAllowlistInputError("This entry is already in the allowlist.");
      return;
    }

    setAllowlistDraft([...allowlistDraft, entry]);
    setAllowlistInput("");
    setAllowlistInputError(null);
  };

  const handleRemoveAllowlistEntry = (entry: string) => {
    setAllowlistDraft(allowlistDraft.filter((item) => item !== entry));
  };

  const handleSaveAllowlist = async () => {
    const success = await appStore.updateApiKeyAllowlist(
      apiKey.id,
      allowlistDraft,
    );
    if (!success) return;

    setAllowlistSaved(true);
    if (allowlistSavedTimeoutRef.current) {
      clearTimeout(allowlistSavedTimeoutRef.current);
    }
    allowlistSavedTimeoutRef.current = setTimeout(() => {
      setAllowlistSaved(false);
    }, SAVED_RESET_MS);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
        IP / CIDR allowlist
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Only requests from these addresses will be accepted. Leave empty to
        allow any address.
      </Typography>

      {allowlistDraft.length > 0 ? (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
          {allowlistDraft.map((entry) => (
            <Chip
              key={entry}
              label={entry}
              size="small"
              disabled={appStore.isUpdatingApiKeyAllowlist}
              onDelete={() => handleRemoveAllowlistEntry(entry)}
              sx={{ fontFamily: "monospace" }}
            />
          ))}
        </Stack>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.5, fontStyle: "italic" }}
        >
          No entries — all IP addresses are currently allowed.
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder="e.g. 203.0.113.10 or 198.51.100.0/24"
          value={allowlistInput}
          disabled={appStore.isUpdatingApiKeyAllowlist}
          onChange={(e) => handleAllowlistInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddAllowlistEntry();
            }
          }}
          error={!!allowlistInputError}
          helperText={allowlistInputError}
        />
        <Button
          onClick={handleAddAllowlistEntry}
          variant="outlined"
          disabled={appStore.isUpdatingApiKeyAllowlist}
          sx={{ flexShrink: 0 }}
        >
          Add
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1.5 }}>
        <Button
          onClick={handleSaveAllowlist}
          variant="contained"
          size="small"
          disabled={!isAllowlistDirty || appStore.isUpdatingApiKeyAllowlist}
        >
          {appStore.isUpdatingApiKeyAllowlist ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} />
              Saving...
            </Box>
          ) : (
            "Save allowlist"
          )}
        </Button>
        {allowlistSaved ? (
          <Typography variant="body2" color="success.main">
            Saved
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
});

export default IpAllowlistBox;
